import React from 'react';
import * as THREE from 'three';
import { invalidate } from '@react-three/fiber';

import { VIEW_CUBE_SELECTOR } from '../Components/ViewCube/ViewCube';

interface UseFlatRotateArgs {
  containerRef: React.RefObject<HTMLDivElement>;
  cameraRef: React.MutableRefObject<THREE.OrthographicCamera | null>;
  controlsRef: React.MutableRefObject<{ enabled: boolean; target: THREE.Vector3; update: () => void } | null>;
  enabled?: boolean;
  marqueeEnabled?: boolean;
}

interface TwistState {
  pending: boolean;
  active: boolean;
  startX: number;
  startY: number;
  lastAngle: number;
  pointerId: number;
  outsideCircle: boolean;
}

const DRAG_THRESHOLD = 4;
const RADIUS_FACTOR = 0.3;

const defaultTwistState: TwistState = {
  pending: false, active: false, startX: 0, startY: 0, lastAngle: 0, pointerId: -1, outsideCircle: false,
};

const useFlatRotate = ({ containerRef, cameraRef, controlsRef, enabled = true, marqueeEnabled = false }: UseFlatRotateArgs) => {
  const twistRef = React.useRef<TwistState>({ ...defaultTwistState });
  const marqueeEnabledRef = React.useRef(marqueeEnabled);
  const [isRotating, setIsRotating] = React.useState(false);

  marqueeEnabledRef.current = marqueeEnabled;

  React.useEffect(() => {
    const container = containerRef.current;
    if (!container || !enabled) return undefined;

    const getCenter = () => {
      const rect = container.getBoundingClientRect();
      return { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 };
    };

    const getRadius = () => window.innerHeight * RADIUS_FACTOR;

    const isOutsideCircle = (clientX: number, clientY: number) => {
      const center = getCenter();
      const dx = clientX - center.x;
      const dy = clientY - center.y;
      const radius = getRadius();
      return dx * dx + dy * dy > radius * radius;
    };

    const handlePointerDown = (e: PointerEvent) => {
      if (e.button !== 0) return;
      if (marqueeEnabledRef.current) return;
      if (e.target instanceof Element && e.target.closest(VIEW_CUBE_SELECTOR)) return;
      twistRef.current = {
        pending: true,
        active: false,
        startX: e.clientX,
        startY: e.clientY,
        lastAngle: 0,
        pointerId: e.pointerId,
        outsideCircle: isOutsideCircle(e.clientX, e.clientY),
      };
    };

    const handlePointerMove = (e: PointerEvent) => {
      const twist = twistRef.current;
      if (e.pointerId !== twist.pointerId) return;
      if (!twist.pending && !twist.active) return;

      const dx = e.clientX - twist.startX;
      const dy = e.clientY - twist.startY;
      const dist = Math.sqrt(dx * dx + dy * dy);

      if (twist.pending && dist >= DRAG_THRESHOLD) {
        twist.pending = false;
        setIsRotating(true);

        if (twist.outsideCircle) {
          twist.active = true;
          const center = getCenter();
          twist.lastAngle = Math.atan2(e.clientY - center.y, e.clientX - center.x);
          const controls = controlsRef.current;
          if (controls) {
            controls.enabled = false;
          }
        }
      }

      if (!twist.active) return;
      if (!cameraRef.current || !controlsRef.current) return;

      const center = getCenter();
      const angle = Math.atan2(e.clientY - center.y, e.clientX - center.x);
      let delta = angle - twist.lastAngle;
      if (delta > Math.PI) delta -= 2 * Math.PI;
      if (delta < -Math.PI) delta += 2 * Math.PI;
      twist.lastAngle = angle;

      const camera = cameraRef.current;
      const { target } = controlsRef.current;
      const viewDir = camera.position.clone().sub(target).normalize();
      const quat = new THREE.Quaternion().setFromAxisAngle(viewDir, delta);

      camera.up.applyQuaternion(quat);
      camera.updateMatrixWorld();
      controlsRef.current.update();
      invalidate();
    };

    const handlePointerUp = (e: PointerEvent) => {
      if (e.pointerId !== twistRef.current.pointerId) return;
      const controls = controlsRef.current;
      if (twistRef.current.active && controls) {
        controls.enabled = true;
      }
      setIsRotating(false);
      twistRef.current = { ...defaultTwistState };
    };

    container.addEventListener('pointerdown', handlePointerDown, true);
    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);
    window.addEventListener('pointercancel', handlePointerUp);

    return () => {
      container.removeEventListener('pointerdown', handlePointerDown, true);
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
      window.removeEventListener('pointercancel', handlePointerUp);
    };
  }, [enabled]);

  return { isRotating };
};

export default useFlatRotate;
